/**
 * NewTemplateDialog — modal para crear una plantilla custom desde cero
 * (B.6.d.1; OT-0055: roles, flujos y metodología).
 *
 * Pide: tipo + metodología + nombre + template_key (sugerido desde el
 * nombre, editable) + descripción opcional + idioma inicial. Submit:
 *   POST /api/v1/templates
 *
 * Metodología (OT-0055): genérica o de una metodología del tenant; el ciclo
 * de vida siempre es de una (el backend lo exige). Se puede crear la
 * metodología ahí mismo. Con metodología, la clave lleva su código:
 * `<prefijo>-<código>-<nombre>` (`TEMPLATE_KEY_METHODOLOGY_PREFIX`).
 *
 * El body lleva una `definition` mínima válida para cada tipo; el editor
 * del tipo la completa después. Tras crear, navega a `/templates/{id}`.
 *
 * `phase` y `subphase` NO aparecen como tipos seleccionables: viven
 * embebidos dentro del lifecycle template. Tampoco `methodology`, el
 * "modo de ejecución" reservado (Doc 06).
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { createTemplate, type TemplateType } from '../../api/templates'
import { useAccessToken } from '../../auth/useAccessToken'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import { METHODOLOGIES_QUERY_KEY, useMethodologies } from '../../hooks/useMethodologies'
import { pushToast } from '../../stores/toastStore'
import { Dialog } from '../Dialog'
import { MethodologyPicker } from './methodologies/MethodologyPicker'
import { normalizeLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../../i18n/config'

type Locale = SupportedLocale
type CreatableType = 'artifact' | 'lifecycle' | 'role' | 'approval_workflow'

const TEMPLATE_TYPES: CreatableType[] = ['artifact', 'lifecycle', 'role', 'approval_workflow']

interface NewTemplateDialogProps {
  open: boolean
  onClose: () => void
  /** Metodología preelegida (p.ej. al crear desde el detalle de una). */
  defaultMethodologyId?: string | null
}

export function NewTemplateDialog(props: NewTemplateDialogProps) {
  // El formulario se monta al abrir: cada apertura arranca limpia.
  if (!props.open) return null
  return <NewTemplateForm {...props} />
}

function NewTemplateForm({ open, onClose, defaultMethodologyId = null }: NewTemplateDialogProps) {
  const { t, i18n } = useTranslation(['templates', 'common'])
  const { accessToken } = useAccessToken()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const apiErrorMessage = useApiErrorMessage()
  const { byId } = useMethodologies()

  const [type, setType] = useState<CreatableType>('artifact')
  const [methodologyId, setMethodologyId] = useState<string | null>(defaultMethodologyId)
  const [name, setName] = useState('')
  // `null` mientras el usuario no la edite: se sugiere desde el nombre.
  const [typedKey, setTypedKey] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [locale, setLocale] = useState<Locale>(normalizeLocale(i18n.language))
  const [submitted, setSubmitted] = useState(false)

  const code = methodologyId ? byId.get(methodologyId)?.code : undefined
  const key = (typedKey ?? suggestKey(type, code, name)).trim()

  const nameError = !name.trim() ? t('create_dialog.errors.name_required') : null
  const methodologyError =
    type === 'lifecycle' && methodologyId === null
      ? t('create_dialog.errors.methodology_required')
      : null
  const keyError = !key
    ? t('create_dialog.errors.key_required')
    : !KEY_PATTERN.test(key)
      ? t('create_dialog.errors.key_invalid')
      : code && !keyBelongsToMethodology(key, code)
        ? t('create_dialog.errors.key_methodology', { example: suggestKey(type, code, name || 'x') })
        : null
  const firstError = nameError ?? methodologyError ?? keyError

  const mutation = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return createTemplate(accessToken, {
        template_type: type,
        template_key: key,
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(methodologyId ? { methodology_id: methodologyId } : {}),
        definition: starterDefinition(type, {
          key,
          name: name.trim(),
          description: description.trim(),
          locale,
          // En el idioma inicial, como el resto del contenido.
          stageName: t('create_dialog.starter_stage_name', { lng: locale }),
        }),
      })
    },
    onSuccess: (template) => {
      pushToast({ kind: 'success', message: t('actions.draft_created') })
      void queryClient.invalidateQueries({ queryKey: ['templates'] })
      void queryClient.invalidateQueries({ queryKey: METHODOLOGIES_QUERY_KEY })
      onClose()
      void navigate({ to: '/templates/$templateId', params: { templateId: template.id } })
    },
  })

  const submitting = mutation.isPending
  const close = () => {
    if (!submitting) onClose()
  }
  const submit = () => {
    setSubmitted(true)
    if (!firstError) mutation.mutate()
  }
  const inputClass =
    'mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60'

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t('create_dialog.title')}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('create_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || (submitted && firstError !== null)}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? t('create_dialog.submitting') : t('create_dialog.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="font-body text-sm text-ink-muted">{t('create_dialog.subtitle')}</p>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">{t('create_dialog.type_label')}</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as CreatableType)}
            disabled={submitting}
            className={inputClass}
          >
            {TEMPLATE_TYPES.map((tt) => (
              <option key={tt} value={tt}>
                {t(`types.${tt}` as 'types.artifact')}
              </option>
            ))}
          </select>
        </label>

        <div>
          <label htmlFor="new-template-methodology" className="text-xs font-medium text-ink-muted">
            {t('create_dialog.methodology_label')}
          </label>
          <MethodologyPicker
            id="new-template-methodology"
            value={methodologyId}
            onChange={setMethodologyId}
            allowGeneric={type !== 'lifecycle'}
            allowCreate
            disabled={submitting}
            className={inputClass}
          />
          <span
            className={`mt-1 block text-[11px] ${submitted && methodologyError ? 'text-critical' : 'text-ink-subtle'}`}
          >
            {submitted && methodologyError
              ? methodologyError
              : type === 'lifecycle'
                ? t('create_dialog.methodology_help_lifecycle')
                : t('create_dialog.methodology_help')}
          </span>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">{t('create_dialog.name_label')}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('create_dialog.name_placeholder')}
            disabled={submitting}
            className={inputClass}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">{t('create_dialog.key_label')}</span>
          <input
            type="text"
            value={key}
            onChange={(e) => setTypedKey(e.target.value)}
            disabled={submitting}
            spellCheck={false}
            autoComplete="off"
            className={`${inputClass} font-mono`}
          />
          <span
            className={`mt-1 block text-[11px] ${submitted && keyError ? 'text-critical' : 'text-ink-subtle'}`}
          >
            {submitted && keyError
              ? keyError
              : typedKey === null
                ? t('create_dialog.key_auto_suggest')
                : t('create_dialog.key_help')}
          </span>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('create_dialog.description_label')}
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('create_dialog.description_placeholder')}
            disabled={submitting}
            rows={3}
            className={`${inputClass} resize-y`}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('create_dialog.language_label')}
          </span>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value as Locale)}
            disabled={submitting}
            className={inputClass}
          >
            {SUPPORTED_LOCALES.map((loc) => (
              <option key={loc} value={loc}>
                {t(`common:languages.${loc}`)} ({loc})
              </option>
            ))}
          </select>
          <span className="mt-1 block text-[11px] text-ink-subtle">
            {t('create_dialog.language_help')}
          </span>
        </label>

        {submitted && nameError ? <p className="text-xs text-critical">{nameError}</p> : null}
        {mutation.error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 text-xs text-critical">
            {apiErrorMessage(mutation.error)}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const KEY_PATTERN = /^[a-z][a-z0-9-]*$/

/** Prefijo y marcador de tipo de las claves del catálogo: `rol-pm`,
 * `tpl-brd`, `tpl-lifecycle-hsl`, `tpl-workflow-presales`. */
const KEY_PARTS: Record<CreatableType, { prefix: string; marker: string | null }> = {
  artifact: { prefix: 'tpl', marker: null },
  lifecycle: { prefix: 'tpl', marker: 'lifecycle' },
  role: { prefix: 'rol', marker: null },
  approval_workflow: { prefix: 'tpl', marker: 'workflow' },
}

/** Clave sugerida: `<prefijo>[-<código>][-<marcador>]-<nombre>`. Con
 * metodología, el código va en el segundo segmento (regla del backend). */
function suggestKey(type: CreatableType, code: string | undefined, name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
  if (!slug) return ''
  const { prefix, marker } = KEY_PARTS[type]
  return [prefix, code, marker, slug].filter(Boolean).join('-')
}

/** Espejo de `key_belongs_to_methodology` (`methodology_rules.py`). */
function keyBelongsToMethodology(key: string, code: string): boolean {
  return new RegExp(`^[a-z][a-z0-9]*-${code}-[a-z0-9][a-z0-9-]*$`).test(key)
}

/** `definition` mínima válida para cada tipo; el editor la completa. */
function starterDefinition(
  type: TemplateType,
  args: {
    key: string
    name: string
    description: string
    locale: Locale
    stageName: string
  },
): Record<string, unknown> {
  const header = {
    name: args.name,
    ...(args.description ? { description: args.description } : {}),
  }
  switch (type) {
    case 'lifecycle':
      // Doc 06 §5: arranca sin fases; el Lifecycle Designer las agrega.
      return {
        template_type: 'lifecycle',
        template_version: '1.0.0',
        is_system_default: false,
        i18n: { [args.locale]: { ...header, phases: [] } },
        phases: [],
      }
    case 'role':
      return {
        template_type: 'role',
        template_version: '1.0.0',
        is_system_default: false,
        i18n: { [args.locale]: { ...header, responsibilities: [] } },
        applicable_scopes: ['internal'],
      }
    case 'approval_workflow':
      // `WorkflowDefinition` exige ≥1 etapa con nombre y roles, y
      // `template_id` igual a la clave. Arranca con la revisión del autor.
      return {
        template_id: args.key,
        template_type: 'approval_workflow',
        template_version: '1.0.0',
        is_system_default: false,
        i18n: { [args.locale]: header },
        stages: [
          {
            order: 1,
            name: { [args.locale]: args.stageName },
            mode: 'parallel',
            required_roles: ['rol-analyst-author'],
            min_approvals: 'all',
          },
        ],
        allow_user_override: true,
      }
    default:
      // Doc 06 §1.1: artefacto sin secciones; el Designer las agrega.
      return {
        template_type: 'artifact',
        template_version: '1.0.0',
        is_system_default: false,
        tags: [],
        i18n: { [args.locale]: { ...header, sections: [] } },
        sections: [],
        validations_global: [],
      }
  }
}
